import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronRight, ArrowRight, Menu, X, Radar, Boxes, Landmark, Network, Cloud, ScanSearch } from "lucide-react";

const SOLUTIONS = [
  { label: "Enterprise Landing Zone", to: "/services/enterprise-landing-zone", icon: Landmark },
  { label: "Enterprise SIEM", to: "/services/enterprise-siem", icon: Network },
  { label: "Enterprise XDR", to: "/services/enterprise-xdr", icon: ScanSearch },
  { label: "Cloud Security", to: "/services/cloud-security", icon: Cloud },
];

const MEGA = [
  {
    id: "solutions",
    label: "Cybersecurity Solutions",
    heading: "Cybersecurity Solutions",
    desc: "Deployable security infrastructure — landing zones, SIEM, XDR and cloud security, engineered and hardened by Lanvaus.",
    grid: [
      { label: "Enterprise Landing Zone", sub: "Secure-by-default cloud foundation", to: "/services/enterprise-landing-zone", icon: Landmark },
      { label: "Enterprise SIEM", sub: "Centralized detection at scale", to: "/services/enterprise-siem", icon: Network },
      { label: "Enterprise XDR", sub: "Unified detection & response", to: "/services/enterprise-xdr", icon: ScanSearch },
      { label: "Cloud Security", sub: "Posture, workloads & identity", to: "/services/cloud-security", icon: Cloud },
    ],
    explore: { label: "Explore All Cybersecurity Solutions", to: "/services/solutions" },
  },
  {
    id: "vapt",
    label: "VAPT",
    heading: "VAPT",
    desc: "Manual-first vulnerability assessment and penetration testing across web, mobile, API, network and cloud — with evidence-grade reporting and a free re-test.",
    explore: { label: "Explore VAPT", to: "/services/vapt" },
  },
  {
    id: "managed",
    label: "Managed Security Services",
    heading: "Managed Security Services",
    desc: "24/7 security operations — run for you as a service, or deployed into your own environment for your team to operate.",
    grid: [
      { label: "SOC as a Service", sub: "Fully managed, remote, 24/7 by our analysts", to: "/services/managed-security", icon: Radar },
      { label: "SOC Deployment", sub: "Deployed inside your environment, run by your team", to: "/services/managed-security", icon: Boxes },
    ],
    explore: { label: "Explore Managed Security Services", to: "/services/managed-security" },
  },
  {
    id: "oss",
    label: "Open Source SOC Model",
    heading: "Open Source SOC Model",
    desc: "Our licensed SOC product built on open-source foundations — DPDP and CERT-In ready, so small Indian companies reach compliance without enterprise licence fees.",
    badge: "Product",
    explore: { label: "Explore Open Source SOC Model", to: "/services/open-source-soc" },
  },
];

const MOBILE_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Industries", to: "/industries" },
  { label: "Resources", to: "/resources" },
  { label: "Contact", to: "/contact" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const linkCls = ({ isActive }) =>
  `whitespace-nowrap rounded-lg px-2.5 py-2 text-[15px] font-medium text-white transition-[background-color] duration-200 hover:bg-white/10 xl:px-3.5 xl:text-base ${
    isActive ? "bg-white/10" : ""
  }`;

const mobileRow =
  "flex min-h-[52px] items-center justify-between rounded-xl px-4 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-white/[0.07] active:bg-white/10";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [activeCat, setActiveCat] = useState("solutions");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const { pathname } = useLocation();
  const headerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setMegaOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const openMega = () => {
    setActiveCat("solutions");
    setMegaOpen(true);
  };

  const active = MEGA.find((c) => c.id === activeCat) || MEGA[0];

  return (
    <header
      ref={headerRef}
      onMouseLeave={() => setMegaOpen(false)}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled ? "shadow-[0_10px_40px_-15px_rgba(0,0,0,0.7)]" : ""
      }`}
      style={{ background: "rgba(65, 79, 120, 0.92)", backdropFilter: "blur(14px)" }}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between gap-3 px-5 sm:h-[76px] sm:px-8 lg:h-[84px] lg:gap-6 lg:px-10 xl:px-16">
        <Link to="/" className="flex min-w-0 shrink-0 items-center gap-2.5 sm:gap-3" data-testid="nav-logo">
          <img
            src="/assets/lanvaus-mark.png"
            alt="Lanvaus logo"
            width="323"
            height="260"
            className="h-7 w-auto shrink-0 sm:h-8 xl:h-9"
          />
          <img
            src="/assets/lanvaus-wordmark.png"
            alt="Lanvaus"
            width="778"
            height="120"
            className="h-[17px] w-auto sm:h-[19px] lg:h-[18px] xl:h-[22px]"
          />        </Link>

        <nav className="hidden min-w-0 items-center lg:flex lg:gap-0.5 xl:gap-2" data-testid="nav-links">
          <NavLink to="/" className={linkCls} data-testid="nav-home-link">
            Home
          </NavLink>

          <div onMouseEnter={openMega} className="shrink-0">
            <button
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-2 text-[15px] font-medium text-white transition-[background-color] duration-200 hover:bg-white/10 xl:px-3.5 xl:text-base ${
                pathname.startsWith("/services") || megaOpen ? "bg-white/10" : ""
              }`}
              data-testid="nav-services-dropdown"
              onClick={() => (megaOpen ? setMegaOpen(false) : openMega())}
            >
              Services
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          <NavLink to="/about" className={linkCls} data-testid="nav-about-link">
            About
          </NavLink>
          <NavLink to="/industries" className={linkCls} data-testid="nav-industries-link">
            Industries
          </NavLink>
          <NavLink to="/resources" className={linkCls} data-testid="nav-resources-link">
            Resources
          </NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact-link">
            Contact
          </NavLink>
        </nav>

        <div className="flex shrink-0 items-center gap-3">
          <Link
            to="/contact"
            className="btn-primary hidden whitespace-nowrap !px-4 !py-2.5 !text-[13px] lg:inline-flex xl:!px-6 xl:!py-3 xl:!text-sm"
            data-testid="nav-get-consultation-btn"
          >
            Get a Consultation
            <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/20 text-white lg:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {megaOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="hidden overflow-hidden border-t border-white/10 lg:block"
            data-testid="services-mega-panel"
          >
            <div className="mx-auto grid min-h-[420px] max-w-7xl grid-cols-[280px_1fr] gap-8 px-6 py-10 lg:grid-cols-[300px_1fr] lg:gap-12 lg:px-16">
              <div className="flex flex-col gap-1.5" data-testid="mega-categories">
                {MEGA.map((cat) => (
                  <Link
                    key={cat.id}
                    to={cat.explore.to}
                    onMouseEnter={() => setActiveCat(cat.id)}
                    onClick={() => setMegaOpen(false)}
                    className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3.5 text-[15px] font-semibold transition-[background-color,color] duration-200 ${
                      activeCat === cat.id
                        ? "bg-[#4C5A8C]/60 text-white"
                        : "text-white/85 hover:bg-white/5 hover:text-white"
                    }`}
                    data-testid={`mega-cat-${cat.id}`}
                  >
                    <span className="flex items-center gap-2.5">
                      {cat.label}
                      {cat.badge && (
                        <span className="rounded-full border border-white/30 bg-white/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
                          {cat.badge}
                        </span>
                      )}
                    </span>
                    <ChevronRight
                      className={`h-4 w-4 transition-colors duration-200 ${
                        activeCat === cat.id ? "text-white" : "text-white/40"
                      }`}
                    />
                  </Link>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, x: 14 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="border-l border-white/10 pl-8 lg:pl-12"
                  data-testid="mega-content"
                >
                  <h3 className="text-xl font-bold text-white" data-testid="mega-heading">
                    {active.heading}
                  </h3>
                  <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/70" data-testid="mega-desc">
                    {active.desc}
                  </p>
                  {active.grid && (
                    <div className="mt-7 grid max-w-2xl gap-4 sm:grid-cols-2">
                      {active.grid.map((g) => (
                        <Link
                          key={g.label}
                          to={g.to}
                          onClick={() => setMegaOpen(false)}
                          className="group flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-5 transition-[background-color,border-color] duration-200 hover:border-white/25 hover:bg-white/[0.08]"
                          data-testid={`mega-tile-${slug(g.label)}`}
                        >
                          <span className="rounded-lg bg-[#4C5A8C]/70 p-2.5">
                            <g.icon className="h-5 w-5 text-white" />
                          </span>
                          <span>
                            <span className="block text-sm font-bold text-white">{g.label}</span>
                            <span className="mt-1 block text-xs leading-snug text-white/65">
                              {g.sub}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                  <Link
                    to={active.explore.to}
                    onClick={() => setMegaOpen(false)}
                    className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#B9C2F2] transition-colors duration-200 hover:text-white"
                    data-testid={`mega-explore-${active.id}`}
                  >
                    {active.explore.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile / tablet slide-in panel (below 1024px). Portalled to body because the
          header's backdrop-filter would otherwise become the containing block. */}
      {createPortal(
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="fixed inset-0 top-[68px] z-[60] bg-black/60 sm:top-[76px] lg:hidden"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-menu-overlay"
              />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                className="fixed bottom-0 right-0 top-[68px] z-[70] w-[min(88vw,380px)] overflow-y-auto overscroll-contain border-l border-white/10 sm:top-[76px] lg:hidden"
                style={{ background: "#0D1322" }}
                data-testid="mobile-menu"
              >
              <div className="flex min-h-full flex-col gap-1.5 px-4 py-5">
                <Link to="/" className={mobileRow} data-testid="mobile-nav-home">
                  Home
                </Link>

                <button
                  className={mobileRow}
                  onClick={() => setMobileServicesOpen((v) => !v)}
                  data-testid="mobile-nav-services-toggle"
                  aria-expanded={mobileServicesOpen}
                >
                  Services
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {mobileServicesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                      data-testid="mobile-services-accordion"
                    >
                      <div className="ml-2 flex flex-col gap-1 border-l border-white/12 pl-2">
                        <Link to="/services" className={`${mobileRow} !text-sm !font-medium text-white/90`} data-testid="mobile-nav-all-services">
                          All Services
                        </Link>

                        <button
                          className={`${mobileRow} !text-sm !font-medium text-white/90`}
                          onClick={() => setMobileSolutionsOpen((v) => !v)}
                          data-testid="mobile-nav-solutions-toggle"
                          aria-expanded={mobileSolutionsOpen}
                        >
                          Cybersecurity Solutions
                          <ChevronDown
                            className={`h-4 w-4 shrink-0 transition-transform duration-300 ${mobileSolutionsOpen ? "rotate-180" : ""}`}
                          />
                        </button>

                        <AnimatePresence initial={false}>
                          {mobileSolutionsOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                              className="overflow-hidden"
                              data-testid="mobile-solutions-accordion"
                            >
                              <div className="ml-2 flex flex-col gap-1 border-l border-white/12 pl-2">
                                {SOLUTIONS.map((s) => (
                                  <Link
                                    key={s.to}
                                    to={s.to}
                                    className="flex min-h-[48px] items-center gap-3 rounded-xl px-4 text-sm text-white/80 transition-colors duration-200 hover:bg-white/[0.07] hover:text-white"
                                    data-testid={`mobile-nav-${slug(s.label)}`}
                                  >
                                    <s.icon className="h-4 w-4 shrink-0 text-[#8E9AEA]" />
                                    {s.label}
                                  </Link>
                                ))}
                                <Link
                                  to="/services/solutions"
                                  className="flex min-h-[48px] items-center gap-2 rounded-xl px-4 text-sm font-semibold text-[#B9C2F2] transition-colors duration-200 hover:bg-white/[0.07] hover:text-white"
                                  data-testid="mobile-nav-solutions-overview"
                                >
                                  Solutions overview
                                  <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <Link to="/services/vapt" className={`${mobileRow} !text-sm !font-medium text-white/90`} data-testid="mobile-nav-vapt">
                          VAPT
                        </Link>
                        <Link to="/services/managed-security" className={`${mobileRow} !text-sm !font-medium text-white/90`} data-testid="mobile-nav-managed-security-services">
                          Managed Security Services
                        </Link>
                        <Link to="/services/open-source-soc" className={`${mobileRow} !text-sm !font-medium text-white/90`} data-testid="mobile-nav-open-source-soc-model">
                          Open Source SOC Model
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {MOBILE_LINKS.slice(1).map((l) => (
                  <Link key={l.to} to={l.to} className={mobileRow} data-testid={`mobile-nav-${slug(l.label)}`}>
                    {l.label}
                  </Link>
                ))}

                <Link to="/contact" className="btn-primary mt-auto !min-h-[52px] w-full" data-testid="mobile-nav-consultation-btn">
                  Get a Consultation
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          </>
        )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
};
